import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, ArrowRight, BarChart3, FileText, Zap, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BetaPage() {
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({
    company_name: '', trade: '', role: '', bid_frequency: '', review_process: '', email: '',
    consent_accepted: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const res = await axios.get(`${API}/api/trades`);
        const list = res.data.trades || [];
        setTrades(list);
        if (list.length > 0 && !form.trade) {
          setForm(f => ({ ...f, trade: list[0].trade_code }));
        }
      } catch { /* fallback to empty */ }
    };
    fetchTrades();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.email) { toast.error('Company name and email required'); return; }
    if (!form.consent_accepted) { toast.error('You must accept the Terms & Conditions and Privacy Policy'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await axios.post(`${API}/api/beta/apply`, fd);
      // Record consent
      await axios.post(`${API}/api/legal/consent/registration`, {
        email: form.email,
        terms_version: '1.0',
        privacy_version: '1.0',
      }).catch(() => {}); // non-blocking
      setSubmitted(true);
      toast.success('Application received');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Submission failed';
      toast.error(msg);
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <Card className="bg-slate-900 border-slate-800 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>Application Received</h2>
            <p className="text-sm text-slate-400">We'll review your application and get back to you within 48 hours with beta access details.</p>
            <a href="/" className="inline-block text-sm text-orange-400 hover:text-orange-300 transition-colors">Back to BidSentry</a>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="beta-page">
      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 pt-16 pb-8">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-orange-500" />
          <span className="text-xs uppercase tracking-[0.2em] text-orange-500 font-medium">BidSentry Beta</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
          Early Access to a Fast Commercial Decision Tool
        </h1>
        <p className="text-base text-slate-400 max-w-2xl">
          Run live bid checks before submission. Spot underpricing. Know your margin position in seconds — not hours.
        </p>
      </div>

      {/* Value Props */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { icon: BarChart3, title: 'Instant Bid Check', desc: 'Three numbers, instant underpricing detection' },
            { icon: FileText, title: 'Before/After Scenarios', desc: 'See how a price change affects your margin and downside' },
            { icon: Zap, title: 'Repeated Use', desc: 'Check every bid before submission — build pricing discipline' },
          ].map((v, i) => (
            <div key={i} className="bg-slate-900 border border-slate-800 rounded-sm p-4">
              <v.icon className="w-4 h-4 text-orange-500 mb-2" />
              <h3 className="text-sm font-bold text-slate-200 mb-1">{v.title}</h3>
              <p className="text-xs text-slate-500">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Application Form */}
      <div className="max-w-lg mx-auto px-4 pb-16">
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-[0.15em] text-slate-400">Apply for Beta Access</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase text-slate-500 tracking-wider mb-1 block">Company Name *</label>
                <Input value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} className="bg-slate-800 border-slate-700 text-slate-200" placeholder="e.g. Apex Electrical Solutions" data-testid="beta-company-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500 tracking-wider mb-1 block">Trade</label>
                <select value={form.trade} onChange={e => setForm(f => ({...f, trade: e.target.value}))} className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm px-3 py-2 rounded-sm" data-testid="beta-trade">
                  {trades.length > 0 ? trades.map(t => (
                    <option key={t.trade_code} value={t.trade_code}>{t.trade_name}</option>
                  )) : (
                    <>
                      <option value="electrical">Electrical</option>
                      <option value="hvac">HVAC</option>
                      <option value="plumbing">Plumbing</option>
                      <option value="telecom">Telecom</option>
                      <option value="civils">Civils</option>
                    </>
                  )}
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-slate-500 tracking-wider mb-1 block">Your Role</label>
                  <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm px-3 py-2 rounded-sm" data-testid="beta-role">
                    <option value="">Select...</option>
                    <option value="estimator">Estimator</option>
                    <option value="director">Director / Owner</option>
                    <option value="commercial_manager">Commercial Manager</option>
                    <option value="project_manager">Project Manager</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500 tracking-wider mb-1 block">Bids Per Month</label>
                  <select value={form.bid_frequency} onChange={e => setForm(f => ({...f, bid_frequency: e.target.value}))} className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm px-3 py-2 rounded-sm" data-testid="beta-bid-frequency">
                    <option value="">Select...</option>
                    <option value="1-2">1–2</option>
                    <option value="3-5">3–5</option>
                    <option value="6-10">6–10</option>
                    <option value="10+">10+</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500 tracking-wider mb-1 block">Current Review Process</label>
                <select value={form.review_process} onChange={e => setForm(f => ({...f, review_process: e.target.value}))} className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm px-3 py-2 rounded-sm" data-testid="beta-review-process">
                  <option value="">How do you check bids now?</option>
                  <option value="gut_feel">Gut feel / experience</option>
                  <option value="spreadsheet">Spreadsheet check</option>
                  <option value="senior_review">Senior review / sign-off</option>
                  <option value="no_review">No formal review</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500 tracking-wider mb-1 block">Email *</label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="bg-slate-800 border-slate-700 text-slate-200" placeholder="you@company.co.uk" data-testid="beta-email" />
              </div>
              <div className="flex items-start gap-2" data-testid="beta-consent-container">
                <input
                  type="checkbox"
                  id="betaConsent"
                  checked={form.consent_accepted}
                  onChange={(e) => setForm(f => ({...f, consent_accepted: e.target.checked}))}
                  className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                  data-testid="beta-consent-checkbox"
                />
                <label htmlFor="betaConsent" className="text-xs text-slate-400">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">Terms &amp; Conditions</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">Privacy Policy</a>
                </label>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white mt-2" data-testid="beta-submit-btn">
                {loading ? 'Submitting...' : <>Apply for Beta Access <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
              </Button>
              <p className="text-[9px] text-slate-600 text-center">Limited to 50 beta users. No credit card required.</p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
